import { z } from 'zod';
/**
 * Files (M8.3, `SECURITY.md` §12.2/§12.3, `DATABASE.md` §7.8 files, `API.md` §2.3/§2.4/§2.8).
 *
 * Three shapes and one rule between them:
 *
 * | Shape | Authority | Table |
 * |---|---|---|
 * | `UploadTicket` | the storage driver | nothing yet - the object does not exist |
 * | `FileSummary` | `files` | `files` |
 * | `DownloadTicket` | the storage driver | `files`, after authorization |
 *
 * **Nothing here is a path.** A client never names, chooses or reads an object key: it
 * asks for a ticket and is handed one. The key is generated server-side as
 * `tenants/<tenant_id>/<yyyy>/<uuid>` and carries no original filename, no patient id
 * and no purpose word, because a key is a string that ends up in access logs, CDN
 * caches and support tickets (rules §28.4, `SECURITY.md` §12.2). That is also why
 * traversal is not something this contract defends against - there is no input to
 * traverse with.
 *
 * **The client's content type is a hint, never a fact.** It is validated up front so a
 * 40 MB upload is refused before it is spent, and then validated *again* on `complete`
 * against the first bytes of the stored object (`SECURITY.md` §12.3). A mismatch
 * rejects the file; it does not correct it.
 */
/* ------------------------------------------------------------------- kinds ---- */
/**
 * `files.purpose`. The word decides the size ceiling, the type allow-list, who may ask
 * for the upload and who may later read it - so it is a closed enum rather than free
 * text, and adding one is a change to `FILE_KIND_RULES` in the API as well.
 */
export const FILE_KINDS = [
    'insurance_card_front',
    'insurance_card_back',
    'attachment',
    'logo',
    'favicon',
    'provider_photo',
    'export',
];
export const FileKindSchema = z.enum(FILE_KINDS);
/** `files.owner_kind`. Which record the object hangs off, for the authorization check. */
export const FILE_OWNER_KINDS = [
    'patient',
    'submission',
    'draft',
    'insurance_policy',
    'branding',
    'provider',
    'appointment',
    'export',
];
export const FileOwnerKindSchema = z.enum(FILE_OWNER_KINDS);
/**
 * `files.status` - the object's lifecycle (`DATABASE.md` §7.8).
 *
 * `pending` means a ticket was issued and nothing may be read; `available` means the
 * bytes are there, sniffed and scanned. A file never goes back to `pending`.
 */
export const FILE_STATUSES = ['pending', 'scanning', 'available', 'rejected', 'deleted'];
export const FileStatusSchema = z.enum(FILE_STATUSES);
/**
 * `files.scan_status` - the malware verdict, separate from the lifecycle on purpose.
 *
 * `status` answers "is this object usable?" and `scan_status` answers "has anything
 * looked at it?". Collapsing them would make "uploaded, sniffed, waiting for the
 * scanner" and "uploaded, sniffed, scanner says it is clean" the same word, and a
 * download gate can only be written against the second (FR-INTAKE-05, rules §28.6).
 */
export const SCAN_STATUSES = ['pending', 'clean', 'infected', 'error', 'skipped'];
export const ScanStatusSchema = z.enum(SCAN_STATUSES);
/**
 * Every content type the platform accepts, anywhere (`SECURITY.md` §12.3).
 *
 * SVG is absent and stays absent: it is a script container that browsers render, and
 * "an image" is the one category where that difference is invisible to the person
 * uploading it.
 */
export const ALLOWED_CONTENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    'application/pdf',
];
export const AllowedContentTypeSchema = z.enum(ALLOWED_CONTENT_TYPES);
/* ------------------------------------------------------------------ shapes ---- */
/**
 * One stored object, as every surface is allowed to see it.
 *
 * `name` is the original filename, decrypted from `files.original_name_enc` for the
 * roles allowed to see it and `null` for everyone else. It is display text: it is never
 * part of a key, a header or a path.
 */
export const FileSummarySchema = z
    .object({
    id: z.uuid(),
    kind: FileKindSchema,
    ownerKind: FileOwnerKindSchema,
    contentType: z.string().max(160),
    sizeBytes: z.number().int().min(0),
    status: FileStatusSchema,
    scanStatus: ScanStatusSchema,
    /** True only when `status = 'available'` and `scanStatus = 'clean'`. */
    downloadable: z.boolean(),
    name: z.string().max(255).nullable().default(null),
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable()
        .default(null),
    createdAt: z.iso.datetime({ offset: true }),
})
    .strict();
/**
 * What a client asks for before it uploads.
 *
 * `sizeBytes` is required and checked against the kind's ceiling *here*, before a
 * ticket exists, so an oversized upload costs one small request rather than a spent
 * transfer and a refusal at the end of it. `fileName` is optional and is stored
 * encrypted; sending it is a courtesy to the patient reading their own record back.
 */
export const UploadUrlRequestSchema = z
    .object({
    kind: FileKindSchema,
    contentType: AllowedContentTypeSchema,
    sizeBytes: z.number().int().min(1),
    fileName: z.string().max(255).optional(),
    /**
     * Which record the object belongs to, when the caller already knows. Ignored on the
     * public funnel (the draft cookie decides) and validated against the caller's own
     * records everywhere else - an id in a body is an id anybody can type (rules §18.4).
     */
    ownerId: z.uuid().optional(),
})
    .strict();
/**
 * The ticket. `uploadUrl` is a bearer credential with a short life: it is the whole
 * authorization for one PUT of one object, so it is never logged and never persisted.
 */
export const UploadTicketSchema = z
    .object({
    fileId: z.uuid(),
    uploadUrl: z.string().max(4096),
    method: z.literal('PUT'),
    /** Headers the client must send verbatim. Empty object when there are none. */
    headers: z.record(z.string().max(64), z.string().max(256)),
    maxSizeBytes: z.number().int().min(1),
    expiresAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const UploadUrlResponseSchema = z.object({ data: UploadTicketSchema });
/**
 * `POST .../files/:id/complete`.
 *
 * `sha256` is optional and is *compared*, never trusted: when the client sends one and
 * it does not match what storage holds, the file is rejected. Omitting it is allowed
 * because a browser hashing a 10 MB image on a phone is a second of jank for a check
 * the server does anyway.
 */
export const CompleteUploadRequestSchema = z
    .object({
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .optional(),
})
    .strict();
export const FileResponseSchema = z.object({ data: z.object({ file: FileSummarySchema }) });
export const FileListResponseSchema = z.object({ data: z.array(FileSummarySchema) });
/** `GET .../files/:id/download-url`. Five minutes, per `SECURITY.md` §12.3. */
export const DownloadTicketSchema = z
    .object({
    url: z.string().max(4096),
    expiresAt: z.iso.datetime({ offset: true }),
    contentType: z.string().max(160),
    sizeBytes: z.number().int().min(0),
})
    .strict();
export const DownloadUrlResponseSchema = z.object({ data: DownloadTicketSchema });
/* ------------------------------------------------------------------- rules ---- */
/**
 * The per-kind ceiling and allow-list, shared so the builder, the renderer and the API
 * cannot disagree about what a patient may attach.
 *
 * `SECURITY.md` §12.3 sets 10 MB as the platform ceiling. A logo is smaller because a
 * 10 MB logo is a mistake rather than a requirement, and an export is larger because it
 * is generated by this platform rather than uploaded to it.
 */
export const FILE_KIND_RULES = Object.freeze({
    insurance_card_front: {
        maxSizeBytes: 10 * 1024 * 1024,
        contentTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'],
    },
    insurance_card_back: {
        maxSizeBytes: 10 * 1024 * 1024,
        contentTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'],
    },
    attachment: {
        maxSizeBytes: 10 * 1024 * 1024,
        contentTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'],
    },
    logo: { maxSizeBytes: 2 * 1024 * 1024, contentTypes: ['image/png', 'image/jpeg'] },
    favicon: { maxSizeBytes: 512 * 1024, contentTypes: ['image/png'] },
    provider_photo: { maxSizeBytes: 5 * 1024 * 1024, contentTypes: ['image/jpeg', 'image/png'] },
    export: { maxSizeBytes: 25 * 1024 * 1024, contentTypes: ['application/pdf'] },
});
/** True when the kind accepts the type at all. The size check is a separate question. */
export function isContentTypeAllowedFor(kind, contentType) {
    return FILE_KIND_RULES[kind].contentTypes.includes(contentType);
}
//# sourceMappingURL=files.js.map